import React, { useState, useEffect, Fragment } from 'react';
import { Map3D } from '@yourhere/yourhere-core'
import { Config, SceneWrapper } from '@yourhere/yourhere-3dmapping'
import './App.scss';
import config from './willowbrook-config.json'
import diacritics from 'diacritics';

// @note: mdi implementation example
// import Icon from '@mdi/react';
// import { mdiMenuDown } from '@mdi/js';

function App() {
  const [activeFloor, setActiveFloor] = useState('GroundLevel')
  const [activeTenant, setActiveTenant] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [cmsData, setCmsData] = useState()
  const [showList, setShowList] = useState(false)
  const [viewMode, setViewMode] = useState('full')
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900)
  const [listings, setListings] = useState()

  const changeTenant = (cmsID, mapSelect = false) => {
    const matchingTenant = cmsData.modTenants.find(t => {
      const {
        locationid = null,
        suite: {
          textfields: {
            suitenum = null
          } = {}
        } = {}
      } = t
      const refID = locationid ? locationid : suitenum
      if (!refID) return
      return `${refID}`.toLowerCase() === `${cmsID}`.toLowerCase()
    })

    // reset list popup in mobile mode
    if (showList) setShowList(false)

    if (!matchingTenant) {
      return
    }
    
    // focus on selected tenant and respective listing
    const floorLookup = { 'Floor 1': 'GroundLevel' }
    const modActiveFloor = floorLookup[matchingTenant.floor.name]
      ? floorLookup[matchingTenant.floor.name] 
      : matchingTenant.floor.name.replace(' ','')

    setActiveFloor(modActiveFloor)
    setActiveTenant(`${cmsID}`)

    // if listing selected from map
    if (mapSelect) {
      // 1 - clear search term
      setSearchTerm('')

      // 2 - update category open state 
      const { category = [] } = matchingTenant
      toggleCategory(category && category.length > 0 ? category : ['general'], true)

      // 3 - focus on list item
      const listElement = document.querySelector('.tenant-button.active')
      if (!listElement) return
      listElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const focusSearch = () => {
    setShowList(true)
  }

  const toggleCategory = (categoryIDs, openOne = false) => {
    const modList = JSON.parse(JSON.stringify(listings))
    const idx = modList.findIndex(a => categoryIDs.includes(a[0]))
    if (openOne) {
      // close all category groups
      modList.forEach((element, i) => {
        modList[i][1].open = false
      })

      // open selected category group
      if (idx !== -1) modList[idx][1].open = true
    } else {
      if (idx !== -1) modList[idx][1].open = !modList[idx][1].open
    }

    setListings(modList)
  }

  useEffect(() => {
    const getTenantData = async () => {
      const baseURL = 'https://cms.yourhere.ca'
      const accountid = '62e014d1259243293c7f3bc0'
      const key = 'BGPsTJ0zg_6XX_N3Qx2vWouS'
      const categoryFilter = ''
    
      const cmsRes = await fetch(`${baseURL}/v1/mapdata/tenants/${accountid}/${categoryFilter}`, {
        method: 'GET',
        headers: {
          'yrh-integration-api-key': key
        }
      })

      const json = await cmsRes.json()
      if (json) {
        setCmsData(json)
      }

      const defaultTenant = new URLSearchParams(window.location.search).get('t')
      const matchingTenant = json.tenants.find(t => t._id === defaultTenant)

      if (matchingTenant) {
        const { locationid, suite = {}, floor = {} } = matchingTenant
        const cmsID = locationid
          ? locationid
          : suite.textfields
            ? suite.textfields.suitenum
            : ''

        const floorLookup = { 'Floor 1': 'GroundLevel' }
        const floorName = floor.name || 'Floor 1'
        const modActiveFloor = floorLookup[floorName]
          ? floorLookup[floorName] 
          : floorName.replace(' ','')

        setViewMode('simple')
        setActiveFloor(modActiveFloor)
        setActiveTenant(`${cmsID}`)
      } else {
        setActiveFloor('GroundLevel')
      }
    }

    if (!cmsData) getTenantData()
    else {
      // 1 - process tenant data
      cmsData.modTenants = cmsData.tenants.map(t => {
        const { name = '', category = [] } = t

        let foundCats = []
        let modCategory = []
        

        // remove redundant parent category
        category.length > 1 && category.forEach(cat => {
          const foundCat = cmsData.categories.find(c => c.label_id === cat)
          foundCats.push(foundCat)
          const { parent = null } = foundCat
          if (parent && foundCats.some(c => c._id === parent)) {
            modCategory.push(foundCat.label_id)
          }
        })

        const altListingNames = {
          'oakberry': 'oakberry-coming-soon',
          'westland insurance': 'westland-insurance',
          'willowbrook dry cleaning': 'willowbrook-drycleaning-alterations',
          'eccotique spa': 'eccotique-spas-salons',
          'toys r us': 'toysrus-babiesrus',
          'virgin mobile': 'virgin-plus'
        }
        
        const special = /[^-a-zA-Z0-9 ]/g
        const spaces = /\s/g

        let parsedName = diacritics.remove(name).toLowerCase().replace(special, '').replace(spaces, '-')

        const altName = Object.keys(altListingNames).find(n => name.toLowerCase().includes(n))
        if (altName) parsedName = altListingNames[altName]

        const modTenant = {
          ...t,
          searchName: diacritics.remove(t.name),
          floor: t.floor || { name: 'Floor 1' }, // set default floor
          category: modCategory.length === 0 ? category : modCategory
        }
        return modTenant
      })  

      // 2 - group listings under their respective category / categories
      let list = {}
      list.general = { open: false, listings: [] }
      cmsData.modTenants
        .filter(t => !searchTerm || [t.searchName].concat(t.category).some(e => e.toLowerCase().includes(searchTerm.toLowerCase())))
        .forEach(t => {
          const { category } = t
          const categoryArr = searchTerm ? [category[0]] : category
          if (categoryArr && categoryArr.length > 0) {
            categoryArr.forEach(cat => {
              if (!list.hasOwnProperty(cat)) list[`${cat}`] = { open: !!searchTerm, listings: [] }
              list[`${cat}`].listings.push(t) 
            })
          } else list.general.listings.push(t)
        })

      
      // 3 - sort listings by category and open first category group
      let modList = Object.entries(list).sort(([idA], [idB]) => idA.localeCompare(idB))
      modList[0][1].open = true
      
      // 4 - commit listings to state
      setListings(modList)
    }
  }, [cmsData, searchTerm])

  useEffect(() => {
    const e = window.addEventListener('resize', () => {
      setShowList(false)
      setIsMobile(window.innerWidth <= 900)
    })

    return () => {
      window.removeEventListener('resize', e)
    }
  }, [])

  if (!cmsData || !listings || !activeFloor) {
    return null
  }

  if (new URLSearchParams(window.location.search).get('t') && !activeTenant) {
    return null
  }

  const clickHandler = (mesh) => {
    if (viewMode === 'simple') return

    const { name } = mesh

    const configData = config.shops.find(s => s.assetId === name)

    if (!configData || !configData.cmsID) {
      return
    }

    changeTenant(configData.cmsID, true)

    if (isMobile) {
      const matchingTenant = cmsData.modTenants.find(t => {
        const {
          locationid = null,
          suite: {
            textfields: {
              suitenum = null
            } = {}
          } = {}
        } = t
        const refID = locationid ? locationid : suitenum
        if (!refID) return
        return `${refID}`.toLowerCase() === `${configData.cmsID}`.toLowerCase()
      })
      if (!matchingTenant) {
        return
      }

      setShowList(true)
      setSearchTerm(matchingTenant.name)
    }

    const listElement = document.querySelector('.tenant-button.active')
    if (!listElement) return
    listElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const amenityIcons = {
    bathroom: `Restroom`,
    administration: `Administration`,
    postoffice: `Post Office`,
    concierge: `Concierge`,
    transit: `Transit`,
    wheelchair: `Wheelchair Accessibility`,
    bikerack: `Bike Rack`,
    stroller: `Stroller & Wheelchair Loans`,
    foodcourt: `Food Court`,
    electriccharger: `Electric Charger`,
    security: `Security`,
    giftcard: `Gift Cards`,
    mothers: `Mother's Room & Family Room`
  }

  if (!cmsData.categories.some(c => c.label_id === 'general')) {
    cmsData.categories.push({
      label_id: 'general',
      name: 'General'
    })
  }

  const subHeading = (id) => {
    const foundCategory = cmsData.categories.find(c => c.label_id === id)
    return foundCategory ? foundCategory.name : 'General'
  }

  return (
    <div className={`App ${viewMode} ${isMobile ? 'mobile' : 'desktop'}`}>
      <div className="sidebar">
        {
          !isMobile ? (
            <div className="header">
              <img id="header-icon" src="/icons/wbMarker.png" alt="wb marker" />
              <h1>Map</h1>
            </div>
          ) : <></>
        }
        <div className="search-section">
          <div className="search-wrapper">
            <input type="text"
              value={searchTerm}
              placeholder="SEARCH"
              onChange={(e) => { setSearchTerm(e.target.value) }}
              onFocus={focusSearch}
              className="shop-search"
            />
          </div>
        </div>
        {
          !isMobile ? (
            <div className='category-header'>
              <h3>Category</h3>
            </div>
          ) : <></>
        }
        <div className="category-container">
          <div className={`tenant-list ${searchTerm ? 'hide-categories' : ''}`}>
            <button className={`tenant-list-button ${showList ? 'up' : 'down'}`} onClick={() => setShowList(!showList)}>
              <div className='tenant-list-button-label'>
                {searchTerm ? 'Shops' : 'Category'}
              </div>
            </button>
            <div className={`tenant-list-wrap ${showList ? 'show' : 'hide'}`}>
              {
                listings
                  .map(([id, catObj]) => (
                    <React.Fragment key={id}>
                      <div className="category-heading subheading" onClick={() => toggleCategory([id])}>
                        <img id='category-icon' className='category-icon' alt='category icon' src={ `/icons/catIcon${catObj.open ? 'Active' : ''}.svg` }></img>
                        { subHeading(id) }
                      </div>
                      <div className={ `category-listings ${catObj.open ? 'open' : 'close'}` }>
                        {
                          catObj.listings
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(t => {
                              const {
                                locationid = null,
                                suite: {
                                  textfields: {
                                    suitenum = ''
                                    /**
                                     * @IDEA:
                                     * set default suitenum if it doesn't exist in data?
                                     * can show proper "no map" message
                                     *  */ 
                                  } = {}
                                } = {},
                                floor: {
                                  name: floornum = ''
                                } = {},
                                name
                              } = t

                              const cmsID = (locationid ? locationid : suitenum).toLowerCase()

                              return (
                                <Fragment key={`${cmsID}-${floornum}-${name}`}>
                                  <button
                                    className={`tenant-button ${`${activeTenant.toUpperCase()}` === `${cmsID.toUpperCase()}` ? 'active' : ''}`}
                                    onClick={() => changeTenant(cmsID)}>
                                    {name}
                                  </button>
                                </Fragment>
                              )
                            })
                        }
                      </div>
                    </React.Fragment>
                  ))
              }
            </div>
          </div>
        </div>
      </div>
      <Map3D
        babylonFilepath='/models/willowbrook.babylon'
        mapping={{
          config: Config,
          sceneWrapper: SceneWrapper
        }}
        config={config}
        tenantData={cmsData.modTenants}
        icons={ Object.fromEntries(Object.keys(amenityIcons).map(a => [ a, `./icons/amenities/marker/${a}.svg` ])) }
        options={{
          markerButton: false,
          hideControls: true,
          hideFloorLabels: true,
          hideNorthMarker: true,
          showRouteSelector: false,
          clickToHighlight: viewMode === 'full',
          centerActiveTenant: true,
          orthoScale:  isMobile
            ? 0.08
            : viewMode === 'simple'
              ? 0.025
              : 0.05,
          upperZoomLimit: isMobile ? 0.15 : 0.06,
          lowerZoomLimit: isMobile ? 0.01 : 0.003,
          cameraBeta: 0.5,
          disableRotationX: true,
          disableRotationY: true,
          logoKey: 'maplogo',
          labelKey: 'mapname',
          labelFont: 'Verdana',
          maxTextLabelSize: 0.015,
          clickHandler,
          floorLabels: {
            'GroundLevel': 'Ground Level'
          },
          zoomSensitivity: {
            mouseWheel: 0.05,
            touch: 0.025
          }
        }}
        activeFloor={activeFloor}
        activeTenant={activeTenant}
        activeAmenities={Object.keys(amenityIcons)}
        primaryHighlight='#2F3236'
        secondaryHighlight='#2F3236'
        backgroundColor='#FFFFFF'
      />
      {
        <div className='legend-container'>
          <div className='legend'>
            <div id='legend-item-default' className='legend-item'>
              <span className='legend-default-label'>LEGEND</span>
            </div>
            {
              Object.keys(amenityIcons).map((k, i) => {
                const label = amenityIcons[k]
                const src = `./icons/amenities/legend/${k}.svg`
                return <div id={ k } className='legend-item' key={ i }>
                  <div className='legend-img'>
                    <img id={ `${k}-icon` } src={ src } alt={ `${k} legend icon` } />
                  </div>
                  <span className='legend-label'>
                    { label }
                  </span>
                </div>
              })
            }
          </div>
        </div>
      }    
    </div>
  );
}

export default App;
